import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../elements/d-label.js';
import { LabeledElement } from '../../abstracts/labeled-element.js';
import type { SelectTagOption } from './select-tag-option';

/**
 *
 *
 * If the single property changes the outer element must also ensure only a single tag is selected.
 *
 * @fires value-changed - Dispatched immediately after changes by the user
 *
 * STATUS OK
 */
@customElement('d-select-tag')
export class DSelectTag extends LabeledElement {
  static readonly styles = [
    ...LabeledElement.styles,
    css`
      :host {
        display: block;
        margin-bottom: -4px;
      }

      :host > div {
        display: flex;
        flex-wrap: wrap;
      }

      .tag {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        box-sizing: border-box;
        min-width: 30px;
        margin-right: 4px;
        margin-bottom: 4px;
        padding: 0 10px;
        height: var(--inputElementHeight);
        background: hsla(0, 0%, 0%, 0.05);
        font-family: var(--mainSans), sans-serif;
        font-size: 13px;
        line-height: 100%;
        color: hsla(0, 0%, 0%, 0.5);
        cursor: pointer;
      }

      .tag-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      /* Tiny icons - same height as text, tag height unchanged */
      :host([icon-size='tiny']) .tag-icon {
        width: 14px;
        height: 14px;
      }

      /* Medium icons - visible but compact */
      :host([icon-size='medium']) .tag-icon {
        width: 26px;
        height: 26px;
      }

      /* Large icons - bigger tag with more padding */
      :host([icon-size='large']) .tag {
        padding: 6px 14px 6px 8px;
        height: auto;
        min-height: var(--inputElementHeight);
      }

      :host([icon-size='large']) .tag-icon {
        width: 36px;
        height: 36px;
      }

      :host([disabled]) .tag {
        opacity: 0.5;
        cursor: default;
      }

      :host([controller]) .tag {
        border: 1px solid var(--borderColor);
        border-radius: 4px;
        background: white;
      }

      :host([outskirts]) .tag {
        border-radius: 4px;
      }

      .tag.toggle-all {
        background-color: hsla(0, 0%, 0%, 0.1);
        font-family: var(--small), sans-serif;
        color: hsla(1, 0%, 0%, 0.7);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 11px;
        font-weight: 500;
      }

      :host:not([disabled]) .tag.toggle-all:hover {
        color: white;
        background-color: hsla(0, 0%, 0%, 0.5);
      }

      .tag[disabled] {
        opacity: 0.5;
        cursor: default;
      }

      .tag[selected] {
        background: var(--themeColor);
        color: white;
      }

      :host([outskirts]) .tag[selected] {
        background: hsla(0, 0%, 0%, 0.25);
      }

      :host([controller]) .tag[selected] {
        border-color: var(--themeColor);
        background: var(--themeColor);
        color: white;
      }

      .tag.toggle-all[selected] {
        color: white;
        background-color: hsla(0, 0%, 0%, 0.5);
      }

      @media (hover: hover) {
        .tag.new:hover {
          background: var(--themeColor);
          color: white;
        }
      }
    `,
  ];
  @property({ type: Array })
  value: string[] = [];
  @property({ type: Array })
  options: SelectTagOption[] = [];
  @property({ type: Boolean, attribute: 'toggle-all', reflect: true })
  toggleAll = false;
  @property({ type: Boolean, attribute: 'not-deselectable', reflect: true })
  notDeselectable = false;
  @property({ type: Boolean, reflect: true })
  single = false;
  @property({ type: Boolean, reflect: true })
  controller = false;
  @property({ type: String, attribute: 'add-new-text' })
  addNewText = '';
  @property({ type: String, attribute: 'icon-size', reflect: true })
  iconSize: 'tiny' | 'medium' | 'large' | '' = '';

  private get allSelected() {
    const selectable = this.options.filter((o) => {
      return !o.disabled || this.value.indexOf(o.value) > -1;
    });
    return selectable.length === this.value.length;
  }

  _selected(value) {
    return (this.value ?? []).includes(value);
  }

  _style(item) {
    if (this._selected(item.value) && item.styleForSelected) {
      return item.styleForSelected;
    }
    return '';
  }

  renderContent() {
    return html`
      <div>
        ${this.toggleAll
          ? html`
              <div class="tag toggle-all" ?selected="${this.allSelected}" @click=${() => this.toggleAllItems()}>
                Alle
              </div>
            `
          : nothing}
        ${this.options.map(
          (item) =>
            html`<div
              class="tag"
              ?disabled=${item.disabled}
              @click=${() => this.toggleItem(item)}
              ?selected="${this._selected(item.value)}"
              style="${this._style(item)}"
            >
              ${item.icon ? html`<img class="tag-icon" src="${item.icon}" alt="" />` : nothing} ${item.text}
            </div>`,
        )}
        ${this.addNewText
          ? html` <div class="tag new" @click=${() => this.addNew()}>${this.addNewText}</div>`
          : nothing}
      </div>
    `;
  }

  private toggleItem(item: SelectTagOption) {
    if (!this.disabled && !item.disabled) {
      let newValue: string[] = [...this.value];
      if (this.value.indexOf(item.value) === -1) {
        if (this.single) {
          newValue = [item.value];
        } else {
          newValue.push(item.value);
        }
      } else if (!this.notDeselectable || this.value.length > 1) {
        newValue = this.value.filter((i) => {
          return i !== item.value;
        });
      }
      this.value = newValue;
      this.dispatchEvent(
        new CustomEvent('value-changed', {
          bubbles: true,
          composed: true,
          detail: { value: this.value },
        }),
      );
    }
  }

  private toggleAllItems() {
    if (!this.disabled) {
      let newValue: string[];
      if (!this.allSelected) {
        newValue = this.options
          .filter((o) => {
            return !o.disabled || this.value.indexOf(o.value) > -1;
          })
          .map((o) => {
            return o.value;
          });
      } else {
        newValue = this.options
          .filter((o) => {
            return o.disabled && this.value.indexOf(o.value) > -1;
          })
          .map((o) => {
            return o.value;
          });
      }
      this.value = newValue;
      this.dispatchEvent(
        new CustomEvent('value-changed', {
          bubbles: true,
          composed: true,
          detail: { value: this.value },
        }),
      );
    }
  }

  private addNew() {
    this.dispatchEvent(
      new CustomEvent('add-new', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-select-tag': DSelectTag;
  }
}
